/**
 * PORTFOLIO TASK DETAIL — the cards under one project, read in place.
 *
 * The roll-up (portfolio.ts) answers "where is this project" in one line. This
 * answers the next question a weekly meeting asks — "what are the actual cards,
 * and what did each person say about theirs" — WITHOUT leaving for the board.
 * It runs on the same cards the roll-up already loaded (store.portfolioIssues),
 * so opening a row costs no read.
 *
 * Pure: it groups a board's cards by status, open work first, and carries onto
 * each card the one line the meeting reads out — its latest update (the card's
 * `stands`, "where this stands") — with what it is waiting on and why folded in
 * beside it. Closed work is summed, not listed, so the drawer stays about the
 * work still in flight. Grouping by phase or by workstream is a later increment;
 * v1 groups by status, which is what a raw development board needs.
 */
import type { Issue, IssueStatus, BlockerType } from './types';
import { STATUS_LABELS } from './types';
import { reallyBlocked, blockerOf } from './blocked';
import { ageInDays } from './card-age';

/** Open statuses, in the order a meeting wants to read them — what is moving,
 *  then what is waiting to be looked at, then what has not started. */
const OPEN_ORDER: IssueStatus[] = ['inprogress', 'staging', 'inreview', 'todo', 'backlog'];
/** Closed statuses — summed into one line, never listed card by card. */
const CLOSED: IssueStatus[] = ['done', 'wontdo'];

const DAY_MS = 86_400_000;

/** Where a status sits, for telling a forward move from a backward one. Both
 *  finished statuses share the top rank (done → won't-do is not a demotion).
 *  Mirrors src/portfolio.ts's own rank. */
const STATUS_RANK: Record<IssueStatus, number> = {
  backlog: 0, todo: 1, inprogress: 2, staging: 3, inreview: 4, done: 5, wontdo: 5,
};

/** The card's latest status transition inside the window, or undefined. Reads the
 *  activity log (the DB writes it for every mover), never `updatedAt` — a text
 *  edit is not a move. */
function latestMove(i: Issue, sinceMs: number, now: number): DetailCard['moved'] {
  let best: { toStatus: IssueStatus; at: number; fromStatus?: IssueStatus } | null = null;
  for (const a of i.activity ?? []) {
    if (a.toStatus == null || a.at < sinceMs || a.at > now) continue;
    if (!best || a.at > best.at) best = { toStatus: a.toStatus, at: a.at, fromStatus: a.fromStatus };
  }
  if (!best) return undefined;
  const to = STATUS_RANK[best.toStatus];
  const from = best.fromStatus != null ? STATUS_RANK[best.fromStatus] : to;
  return { toStatus: best.toStatus, at: best.at, back: to < from };
}

/** What a single card is waiting on — its own blocker, read the same way the
 *  roll-up reads the project's (blocked text → named target → "something"). */
export interface DetailWaiting {
  type: BlockerType | '';
  /** What it waits on, in a few words. */
  label: string;
  /** How long it has waited, in days, or null when the stamp is missing. */
  days: number | null;
}

/** One card in the drawer. Names are left raw (the assignee address); the
 *  component resolves them to a shown name, as the roll-up does for owners. */
export interface DetailCard {
  id: string;
  ref: string;
  title: string;
  status: IssueStatus;
  /** The assignee, raw; '' when nobody holds it. */
  who: string;
  /** The card's latest update — its `stands` — trimmed; '' when none yet. */
  update: string;
  /** The due day, YYYY-MM-DD, when set. */
  dueDay?: string;
  /** Days past due when the card is open and overdue; 0 otherwise. */
  overdueDays: number;
  /** The card's own blocker, when it really is blocked. */
  waiting?: DetailWaiting;
  /** The card's latest STATUS transition within the last 7 days — a real move
   *  between columns, read from the activity log, not a "touched" flag. Absent
   *  when the card has not changed column in the window (a silent text edit does
   *  not count). `back` is true when the move was a demotion or a reopen. */
  moved?: { toStatus: IssueStatus; at: number; back: boolean };
}

/** A status section of the drawer, only present when it has cards. */
export interface DetailGroup {
  status: IssueStatus;
  label: string;
  cards: DetailCard[];
}

/** The whole drawer for one board. */
export interface TaskDetail {
  /** Open sections, in OPEN_ORDER, each non-empty. */
  groups: DetailGroup[];
  /** Every card on the board. */
  total: number;
  /** Open cards (not done/wontdo). */
  openCount: number;
  /** Closed cards (done + wontdo). */
  doneCount: number;
  /** Titles of cards closed within the last week, for the "Done this cycle"
   *  summary line — capped, so the line stays one line. */
  doneTitles: string[];
}

/** The day a `YYYY-MM-DD` falls on, as UTC midnight ms — for an overdue diff. */
function dayMs(iso: string): number | null {
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return null;
  return Date.UTC(y, m - 1, d);
}

/**
 * Build the drawer for one board's cards. `now` is injectable for tests.
 * Within a section the cards that need the meeting's attention lead: blocked
 * first, then the soonest due, then the rest in the order they came.
 */
export function buildTaskDetail(issues: Issue[], opts: { now?: number } = {}): TaskDetail {
  const now = opts.now ?? Date.now();
  const today = Math.floor(now / DAY_MS) * DAY_MS; // UTC midnight of "today"
  const weekAgo = now - 7 * DAY_MS;

  const blockedIds = new Set(reallyBlocked(issues).map((i) => i.id));

  const toCard = (i: Issue): DetailCard => {
    const dueDay = i.dueDate || undefined;
    let overdueDays = 0;
    const open = !CLOSED.includes(i.status);
    if (open && dueDay) {
      const due = dayMs(dueDay);
      if (due != null && due < today) overdueDays = Math.round((today - due) / DAY_MS);
    }
    let waiting: DetailWaiting | undefined;
    if (blockedIds.has(i.id)) {
      waiting = {
        type: (i.blockerType ?? '') as BlockerType | '',
        label: blockerOf(i) || i.blockerTarget?.trim() || 'something',
        days: ageInDays(i.blockedAt, now),
      };
    }
    return {
      id: i.id,
      ref: i.ref,
      title: i.title,
      status: i.status,
      who: (i.assignee ?? '').trim(),
      update: (i.stands ?? '').trim(),
      dueDay,
      overdueDays,
      waiting,
      moved: latestMove(i, weekAgo, now),
    };
  };

  // Attention order inside a section: blocked, then soonest due, then as-is.
  const rank = (c: DetailCard): [number, number] => [
    c.waiting ? 0 : 1,
    c.dueDay ? (dayMs(c.dueDay) ?? Number.MAX_SAFE_INTEGER) : Number.MAX_SAFE_INTEGER,
  ];
  const byAttention = (a: DetailCard, b: DetailCard) => {
    const [aw, ad] = rank(a);
    const [bw, bd] = rank(b);
    return aw - bw || ad - bd;
  };

  const groups: DetailGroup[] = [];
  for (const status of OPEN_ORDER) {
    const cards = issues.filter((i) => i.status === status).map(toCard).sort(byAttention);
    if (cards.length) groups.push({ status, label: STATUS_LABELS[status] ?? status, cards });
  }

  const closed = issues.filter((i) => CLOSED.includes(i.status));
  const doneTitles = closed
    .filter((i) => (i.updatedAt ?? 0) >= weekAgo)
    .map((i) => i.title)
    .slice(0, 4);

  const openCount = issues.length - closed.length;
  return { groups, total: issues.length, openCount, doneCount: closed.length, doneTitles };
}
